// The example plugin, shipped first-party (trusted tier) so the whole concept is
// demonstrable in any build. It is written in the SAME format a third-party
// plugin uses: a manifest (declarative contributions + a declared, optional
// permission with a reason) and a module that only ever touches the host through
// the `slate` API. See decision D3.
//
// This is a FULL demo of every surface a manifest can expose: commands, an
// activity-bar panel (a controls surface + an actionable tree), a header button,
// a bottom-panel tool surface, and a modal surface, plus a consented file read.

import type { PluginManifest, PluginModule } from '@slate/plugin-api';

export const EXAMPLE_PLUGIN_ID = 'slate.hello';

export const EXAMPLE_MANIFEST: PluginManifest = {
  id: EXAMPLE_PLUGIN_ID,
  name: 'Hello (Example)',
  version: '1.0.0',
  publisher: 'Slate',
  description:
    'A full demo: commands, an activity-bar panel with controls, a header button, a bottom-panel tool, and a consented file read.',
  engines: { slate: '^1.0.0' },
  permissions: [
    {
      capability: 'fs.read',
      optional: true,
      reason: 'Read a file you choose so the example can report its size.',
    },
  ],
  contributes: {
    commands: [
      { id: 'hello.greet', title: 'Hello: Greet' },
      { id: 'hello.fileSize', title: 'Hello: Report file size' },
      { id: 'hello.openAbout', title: 'Hello: About (modal surface)' },
      { id: 'hello.pingPanel', title: 'Hello: Ping the controls panel' },
    ],
    activityBar: [{ id: 'hello.bar', title: 'Hello', icon: 'Sparkles', view: 'hello.view' }],
    views: [
      { id: 'hello.panel', container: 'hello.bar', title: 'Hello Controls', kind: 'surface' },
      { id: 'hello.view', container: 'hello.bar', title: 'Hello Actions', kind: 'tree' },
    ],
    topBarActions: [
      {
        id: 'hello.header',
        icon: 'Sparkles',
        title: 'Hello: Greet from the header',
        command: 'hello.greet',
        placement: 'beforeHelp',
      },
    ],
    tools: [{ id: 'hello.tool', title: 'Hello Tool', icon: 'Wand2' }],
    surfaces: [
      { id: 'hello.panel', path: 'surfaces/panel.html', type: 'panel' },
      { id: 'hello.tool', path: 'surfaces/tool.html', type: 'panel' },
      { id: 'hello.about', path: 'surfaces/about.html', type: 'modal' },
    ],
  },
};

export const exampleModule: PluginModule = {
  activate(ctx) {
    const { slate } = ctx;

    ctx.subscriptions.push(
      slate.commands.register('hello.greet', async () => {
        await slate.window.showMessage('Hello from a sandboxed plugin!');
        return 'greeted';
      }),
    );

    // Demonstrates the FULL secure path: ask for the capability (just-in-time
    // consent), then read only if granted. Without the grant the broker refuses.
    ctx.subscriptions.push(
      slate.commands.register('hello.fileSize', async (path?: unknown) => {
        const target = typeof path === 'string' && path.length > 0 ? path : '/etc/hostname';
        const granted = await slate.permissions.request('fs.read');
        if (!granted) {
          await slate.window.showMessage('Permission denied, so the file was not read.');
          return null;
        }
        const text = await slate.fs.read(target);
        await slate.window.showMessage(`"${target}" is ${text.length} characters.`);
        return text.length;
      }),
    );

    // Actionable + collapsible tree. Leaf rows carry the command id to run; the
    // host's PluginTreeView launches them. The 'hello.more' row expands to two
    // more actions.
    ctx.subscriptions.push(
      slate.views.registerTreeDataProvider('hello.view', {
        getChildren: (parentId) => {
          if (parentId === 'hello.more') {
            return [
              { id: 'hello.openAbout', label: 'About (modal surface)' },
              { id: 'hello.pingPanel', label: 'Ping the controls panel' },
            ];
          }
          return [
            { id: 'hello.greet', label: 'Greet' },
            { id: 'hello.fileSize', label: 'Report file size' },
            { id: 'hello.more', label: 'More actions', collapsible: true },
          ];
        },
      }),
    );

    // The activity-bar controls surface (hello.panel). It echoes 'clicked' pings
    // and also handles structured {type:'action', id} messages from its buttons.
    const panel = slate.views.registerSurfaceView('hello.panel');
    ctx.subscriptions.push(panel);
    ctx.subscriptions.push(
      panel.onMessage((msg) => {
        const m = msg as { type?: string; id?: string };
        if (m.type === 'clicked') {
          panel.postMessage({ type: 'echo', of: m.id });
        } else if (m.type === 'action') {
          if (m.id === 'greet') {
            void slate.window.showMessage('Hello from the controls panel!');
          } else if (m.id === 'about') {
            void slate.window.showModal('hello.about');
          }
        }
      }),
    );

    ctx.subscriptions.push(
      slate.commands.register('hello.pingPanel', () => {
        panel.postMessage({ greeting: 'hi from worker' });
        return 'pinged';
      }),
    );

    // The bottom-panel tool surface (hello.tool). Its buttons greet and report
    // the active document's size.
    const tool = slate.views.registerSurfaceView('hello.tool');
    ctx.subscriptions.push(tool);
    ctx.subscriptions.push(
      tool.onMessage((msg) => {
        const m = msg as { type?: string; id?: string };
        if (m.type !== 'action') return;
        if (m.id === 'greet') {
          void slate.window.showMessage('Hello from the bottom-panel tool!');
        } else if (m.id === 'activeSize') {
          void slate.editor.getActiveText().then((t) =>
            slate.window.showMessage(
              t ? `Active document has ${t.length} characters.` : 'No active document.',
            ),
          );
        }
      }),
    );

    ctx.subscriptions.push(
      slate.commands.register('hello.openAbout', async () => {
        await slate.window.showModal('hello.about');
        return 'closed';
      }),
    );
  },
};
